import { useState, useEffect } from 'react'
import { Button, Modal, Select, Tag, Input } from 'antd'
import { MailOutlined, SendOutlined } from '@ant-design/icons'
import {
  usePracticeAgentRecipients,
  usePracticeAgentSendCommunicationMutation,
  usePracticeAgentRewriteMessageMutation,
} from '../../hooks'
import { useToast } from '../../../../contexts/ToastContext'
import type { PracticeAgentCommunicationType } from '../../types'

const { TextArea } = Input

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  let binary = ''
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const slice = bytes.subarray(index, index + chunkSize)
    binary += String.fromCharCode(...slice)
  }
  return btoa(binary)
}

function chunkBase64(input: string, lineLength = 76): string {
  const chunks: string[] = []
  for (let i = 0; i < input.length; i += lineLength) {
    chunks.push(input.slice(i, i + lineLength))
  }
  return chunks.join('\r\n')
}

async function downloadEml(opts: {
  to: string[]
  subject: string
  body: string
  attachments?: Array<{ filename: string; mimeType: string; blob: Blob }>
}) {
  const attachments = opts.attachments ?? []
  const hasAttachments = attachments.length > 0
  const boundary = `----=_Part_${Date.now()}_${Math.random().toString(36).slice(2)}`

  const emlLines: string[] = [
    'MIME-Version: 1.0',
    'X-Unsent: 1',
    `To: ${opts.to.join(', ')}`,
    `Subject: ${opts.subject}`,
  ]

  if (!hasAttachments) {
    emlLines.push('Content-Type: text/plain; charset=UTF-8', '', opts.body)
  } else {
    emlLines.push(
      `Content-Type: multipart/mixed; boundary="${boundary}"`,
      '',
      `--${boundary}`,
      'Content-Type: text/plain; charset=UTF-8',
      'Content-Transfer-Encoding: 7bit',
      '',
      opts.body,
      '',
    )

    for (const attachment of attachments) {
      const base64 = await blobToBase64(attachment.blob)
      emlLines.push(
        `--${boundary}`,
        `Content-Type: ${attachment.mimeType}; name="${attachment.filename}"`,
        `Content-Disposition: attachment; filename="${attachment.filename}"`,
        'Content-Transfer-Encoding: base64',
        '',
        chunkBase64(base64),
        '',
      )
    }

    emlLines.push(`--${boundary}--`)
  }

  const emlContent = emlLines.join('\r\n')
  const blob = new Blob([emlContent], { type: 'message/rfc822' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${opts.subject.replace(/[^a-z0-9 ]/gi, '_').slice(0, 60)}.eml`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

const COMM_TYPE_DEFAULTS: Record<PracticeAgentCommunicationType, { subject: string; body: string }> = {
  flag: {
    subject: 'Flagged for Your Attention',
    body: "Heads up — I wanted to flag the following for your awareness.\n\nPlease let me know if you'd like to discuss further.",
  },
  request: {
    subject: 'Request for Action',
    body: "I'd like to request your assistance with the following.\n\nCould you please review and let me know how you'd like to proceed?",
  },
  escalate: {
    subject: 'Escalation: Immediate Attention Required',
    body: "I'm escalating this matter as it requires immediate attention.\n\nPlease prioritise reviewing and taking action on the following as soon as possible.",
  },
  inform: {
    subject: 'For Your Information',
    body: "I wanted to keep you informed about the following.\n\nNo immediate action is required at this time.",
  },
}

export function CommunicateModal({
  open,
  onClose,
  clientId,
  reportBuilderId,
  reportBuilderGroupId,
  generatePdfBlob,
  pdfFilename,
}: {
  open: boolean
  onClose: () => void
  clientId: string
  reportBuilderId?: string
  reportBuilderGroupId?: string
  generatePdfBlob?: () => Promise<Blob | null>
  pdfFilename?: string
}) {
  const { addToast } = useToast()
  const { data: recipients } = usePracticeAgentRecipients(clientId)
  const sendMutation = usePracticeAgentSendCommunicationMutation()
  const rewriteMutation = usePracticeAgentRewriteMessageMutation()

  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([])
  const [commType, setCommType] = useState<PracticeAgentCommunicationType>('flag')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [isSending, setIsSending] = useState(false)

  useEffect(() => {
    if (open) {
      setSelectedRecipients([])
      setCommType('flag')
      setSubject(COMM_TYPE_DEFAULTS.flag.subject)
      setBody(COMM_TYPE_DEFAULTS.flag.body)
    } else {
      setSelectedRecipients([])
      setSubject('')
      setBody('')
      setCommType('flag')
    }
  }, [open])

  function handleSend() {
    if (selectedRecipients.length === 0) { addToast('Please select at least one recipient.', 'warning'); return }

    setIsSending(true)

    const pdfBlobPromise: Promise<Blob | null> = generatePdfBlob
      ? Promise.race([
          generatePdfBlob().catch(() => null),
          new Promise<null>((res) => setTimeout(() => res(null), 60_000)),
        ])
      : Promise.resolve(null)

    sendMutation.mutate(
      { recipientIds: selectedRecipients, subject, body, reportBuilderId, reportBuilderGroupId, clientId, type: commType },
      {
        onSuccess: () => {
          void (async () => {
            try {
              const pdfBlob = await pdfBlobPromise

              const toEmails = (recipients ?? [])
                .filter((r) => selectedRecipients.includes(r.id))
                .map((r) => r.email)
              try {
                await downloadEml({
                  to: toEmails,
                  subject,
                  body,
                  attachments: pdfBlob
                    ? [{ filename: pdfFilename ?? 'workspace-report.pdf', mimeType: 'application/pdf', blob: pdfBlob }]
                    : [],
                })
              } catch {
                // EML download failed — still complete the send flow
              }
              addToast('Communication sent. Email template downloaded.', 'success')
              onClose()
            } finally {
              setIsSending(false)
            }
          })()
        },
        onError: () => {
          setIsSending(false)
          addToast('Failed to send.', 'error')
        },
      },
    )
  }

  function handleRewrite(tone: 'formal' | 'concise' | 'urgent') {
    if (!body.trim()) {
      addToast('Please enter a message before rewriting.', 'warning')
      return
    }
    rewriteMutation.mutate(
      { body, tone },
      { onSuccess: (res) => setBody(res.body), onError: () => addToast('Failed to rewrite.', 'error') },
    )
  }

  function handleTypeChange(type: PracticeAgentCommunicationType) {
    setCommType(type)
    const defaults = COMM_TYPE_DEFAULTS[type]
    setSubject(defaults.subject)
    setBody(defaults.body)
  }

  const recipientOptions = (recipients ?? []).map((r) => ({ value: r.id, label: `${r.name} — ${r.role}` })).sort((a, b) => a.label.localeCompare(b.label))
  const typeOptions: Array<{ value: PracticeAgentCommunicationType; label: string; color: string }> = [
    { value: 'flag', label: 'Flag', color: 'orange' },
    { value: 'request', label: 'Request', color: 'blue' },
    { value: 'escalate', label: 'Escalate', color: 'red' },
    { value: 'inform', label: 'Inform', color: 'default' },
  ]

  return (
    <Modal
      title={<div className="flex items-center gap-2"><MailOutlined className="feature-icon-accent" /><span>Communicate</span></div>}
      open={open}
      onCancel={isSending ? undefined : onClose}
      closable={!isSending}
      mask={{ closable: !isSending }}
      keyboard={!isSending}
      width={680}
      footer={null}
    >
      <div className="space-y-4 pt-2">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Recipients</label>
          <Select mode="multiple" options={recipientOptions} value={selectedRecipients} onChange={setSelectedRecipients} placeholder="Select recipients…" className="w-full" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Communication Type</label>
          <div className="flex gap-2 flex-wrap">
            {typeOptions.map((t) => (
              <Tag key={t.value} color={commType === t.value ? t.color : 'default'} className={`cursor-pointer px-3 py-1 text-sm ${commType === t.value ? 'ring-1 ring-offset-1' : 'opacity-60'}`} onClick={() => handleTypeChange(t.value)}>
                {t.label}
              </Tag>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-gray-500">Message</label>
            <div className="flex gap-1.5">
              <span className="text-xs text-gray-400 mr-1">Rewrite:</span>
              {(['formal', 'concise', 'urgent'] as const).map((tone) => (
                <Button key={tone} size="small" type="text" onClick={() => handleRewrite(tone)} loading={rewriteMutation.isPending} className="text-xs text-gray-500 capitalize">{tone}</Button>
              ))}
            </div>
          </div>
          <TextArea value={body} onChange={(e) => setBody(e.target.value)} rows={7} placeholder="Your message…" />
        </div>
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <span className="feature-soft-text">●</span>
          Current data view and AI insight will be attached automatically.
        </p>
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" icon={<SendOutlined />} onClick={handleSend} loading={sendMutation.isPending || isSending}>Send</Button>
        </div>
      </div>
    </Modal>
  )
}
